import {
  ApplicationRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  PLATFORM_ID,
  createComponent,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ToastTone = 'success' | 'error' | 'info';

/** id of the outlet's live region; an open dialog claims it with aria-owns. */
export const TOAST_LIST_ID = 'bk-toast-list';

export interface ToastOptions {
  /** Short bold line above the message. Optional; most toasts need none. */
  title?: string;
  /**
   * Milliseconds before it clears itself. 0 keeps it until dismissed.
   * Defaults to 4 s for success and info; errors stay until dismissed.
   */
  duration?: number;
}

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  message: string;
  /** Set while the exit animation plays. */
  leaving: boolean;
}

interface ToastTimer {
  handle?: ReturnType<typeof setTimeout>;
  remaining: number;
  startedAt: number;
}

/**
 * One rail of short confirmations and errors, bottom-right on desktop and
 * full width at the bottom on phones.
 *
 *   toast.success('Scheduled for 14:00 to 1,240 recipients');
 *   toast.error('Send failed: the sender ID was rejected. Pick another sender ID and try again.');
 *
 * Confirmations clear after 4 s and pause while hovered or focused; errors
 * stay until dismissed, so they can be read at any pace. At most three show
 * at once (the oldest goes first). The outlet mounts itself on first
 * use, so no page has to place it; it can also be placed once in a template
 * as <bk-toast-outlet />.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  static readonly DURATION = 4000;
  static readonly MAX_VISIBLE = 3;
  /** Matches the exit animation in toast-outlet.component.css. */
  static readonly LEAVE_MS = 150;

  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly list = signal<readonly Toast[]>([]);
  /** What the outlet renders. */
  readonly toasts = this.list.asReadonly();

  private readonly timers = new Map<number, ToastTimer>();
  private nextId = 1;
  private outletMounted = false;
  private mounting?: Promise<void>;

  success(message: string, options?: ToastOptions): number {
    return this.show('success', message, options);
  }

  error(message: string, options?: ToastOptions): number {
    return this.show('error', message, options);
  }

  info(message: string, options?: ToastOptions): number {
    return this.show('info', message, options);
  }

  show(tone: ToastTone, message: string, options: ToastOptions = {}): number {
    const text = (message ?? '').trim();
    if (!this.isBrowser || !text) return 0;
    this.ensureOutlet();

    const id = this.nextId++;
    const live = this.list().filter((toast) => !toast.leaving);
    // Make room: the oldest visible toast steps out.
    if (live.length >= ToastService.MAX_VISIBLE) {
      live.slice(0, live.length - ToastService.MAX_VISIBLE + 1).forEach((toast) => this.dismiss(toast.id));
    }

    this.list.update((toasts) => [
      ...toasts,
      { id, tone, title: (options.title ?? '').trim(), message: text, leaving: false },
    ]);

    const duration = options.duration ?? (tone === 'error' ? 0 : ToastService.DURATION);
    if (duration > 0) {
      this.timers.set(id, { remaining: duration, startedAt: 0 });
      this.resume(id);
    }
    return id;
  }

  dismiss(id: number): void {
    const toast = this.list().find((item) => item.id === id);
    if (!toast || toast.leaving) return;
    this.stopTimer(id);
    this.timers.delete(id);
    this.list.update((toasts) => toasts.map((item) => (item.id === id ? { ...item, leaving: true } : item)));
    setTimeout(() => {
      this.list.update((toasts) => toasts.filter((item) => item.id !== id));
    }, ToastService.LEAVE_MS);
  }

  clear(): void {
    this.list().forEach((toast) => this.dismiss(toast.id));
  }

  /** Hover or focus: hold the toast where it is. */
  pause(id: number): void {
    const timer = this.timers.get(id);
    if (!timer?.handle) return;
    clearTimeout(timer.handle);
    timer.handle = undefined;
    timer.remaining = Math.max(1000, timer.remaining - (Date.now() - timer.startedAt));
  }

  resume(id: number): void {
    const timer = this.timers.get(id);
    if (!timer || timer.handle) return;
    timer.startedAt = Date.now();
    timer.handle = setTimeout(() => this.dismiss(id), timer.remaining);
  }

  /** Called by an outlet placed in a template, so the service does not add a second one. */
  registerOutlet(): void {
    this.outletMounted = true;
  }

  private stopTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer?.handle) clearTimeout(timer.handle);
  }

  /** Add the outlet to <body> the first time a toast is shown. */
  private ensureOutlet(): void {
    if (this.outletMounted || this.mounting || typeof document === 'undefined') return;
    this.mounting = import('../../components/ui/toast-outlet/toast-outlet.component')
      .then(({ ToastOutletComponent }) => {
        if (this.outletMounted) return;
        this.zone.run(() => {
          const ref = createComponent(ToastOutletComponent, { environmentInjector: this.injector });
          this.appRef.attachView(ref.hostView);
          document.body.appendChild(ref.location.nativeElement);
          ref.changeDetectorRef.detectChanges();
        });
      })
      .catch(() => {
        // Without the outlet a toast is simply not shown; let the next one try again.
        this.mounting = undefined;
      });
  }
}
